import { useMemo } from 'react';

const OFFLINE_TIMEOUT = 11 * 60;

const useFormattedData = (chartData) => {
  return useMemo(() => {
    if (!chartData || !chartData.data) return null;

    // Variables to store previous values for fallback if a field is missing
    let lastThresholdOn = null;
    let lastThresholdOff = null;
    let lastContactorStatus = null;
    let lastOverruleStatus = null;
    let lastMessageTimestamp = 0;

    // Reduce raw data into separate arrays for each series
    const arrays = chartData.data.reduce((acc, msg) => {
      // Calculate the message timestamp in seconds
      const msgSec = Math.round(msg.timestamp / 1000);
      acc.timestamps.push(msgSec);

      // Temperature fields (or null, if not provided)
      const { temperature = {} } = msg;
      acc.temperatureCurrentValues.push(temperature.current ?? null);
      acc.temperatureLowValues.push(temperature.min ?? null);
      acc.temperatureHighValues.push(temperature.max ?? null);

      // Threshold values, with fallback on previous values (if within the offline timeout window)
      if (msg.threshold?.on !== undefined) {
        lastThresholdOn = msg.threshold.on;
        acc.thresholdOnValues.push(msg.threshold.on);
      } else {
        acc.thresholdOnValues.push(msgSec <= lastMessageTimestamp + OFFLINE_TIMEOUT ? lastThresholdOn : null);
      }
      if (msg.threshold?.off !== undefined) {
        lastThresholdOff = msg.threshold.off;
        acc.thresholdOffValues.push(msg.threshold.off);
      } else {
        acc.thresholdOffValues.push(msgSec <= lastMessageTimestamp + OFFLINE_TIMEOUT ? lastThresholdOff : null);
      }

      // Contactor status ("On" translates to 1, otherwise 0)
      if (msg.contactorStatus) {
        lastContactorStatus = msg.contactorStatus === 'On' ? 1 : 0;
        acc.contactorStatusValues.push(lastContactorStatus);
      } else {
        acc.contactorStatusValues.push(msgSec <= lastMessageTimestamp + OFFLINE_TIMEOUT ? lastContactorStatus : null);
      }

      // Overrule status ("On" translates to 1, otherwise 0)
      if (msg.overruleStatus) {
        lastOverruleStatus = msg.overruleStatus === 'On' ? 1 : 0;
        acc.overruleStatusValues.push(lastOverruleStatus);
      } else {
        acc.overruleStatusValues.push(msgSec <= lastMessageTimestamp + OFFLINE_TIMEOUT ? lastOverruleStatus : null);
      }

      // Assume that data from a message means the device is connected (1)
      acc.isConnectedValues.push(1);

      lastMessageTimestamp = msgSec;
      return acc;
    }, {
      timestamps: [],
      temperatureCurrentValues: [],
      temperatureLowValues: [],
      temperatureHighValues: [],
      thresholdOnValues: [],
      thresholdOffValues: [],
      contactorStatusValues: [],
      overruleStatusValues: [],
      isConnectedValues: []
    });

    // Ensure that the start and end of the time range are included in the data arrays
    const startSec = Math.round(chartData.timeRange.from / 1000);
    const endSec = Math.round(chartData.timeRange.to / 1000);

    if (arrays.timestamps[0] !== startSec) {
      arrays.timestamps.unshift(startSec);
      arrays.temperatureCurrentValues.unshift(null);
      arrays.temperatureLowValues.unshift(null);
      arrays.temperatureHighValues.unshift(null);
      arrays.thresholdOnValues.unshift(arrays.thresholdOnValues[0]);
      arrays.thresholdOffValues.unshift(arrays.thresholdOffValues[0]);
      arrays.contactorStatusValues.unshift(arrays.contactorStatusValues[0]);
      arrays.overruleStatusValues.unshift(arrays.overruleStatusValues[0]);
      arrays.isConnectedValues.unshift(arrays.isConnectedValues[0]);
    }

    if (arrays.timestamps[arrays.timestamps.length - 1] !== endSec) {
      arrays.timestamps.push(endSec);
      arrays.temperatureCurrentValues.push(null);
      arrays.temperatureLowValues.push(null);
      arrays.temperatureHighValues.push(null);
      arrays.thresholdOnValues.push(arrays.thresholdOnValues[arrays.thresholdOnValues.length - 1]);
      arrays.thresholdOffValues.push(arrays.thresholdOffValues[arrays.thresholdOffValues.length - 1]);
      arrays.contactorStatusValues.push(arrays.contactorStatusValues[arrays.contactorStatusValues.length - 1]);
      arrays.overruleStatusValues.push(arrays.overruleStatusValues[arrays.overruleStatusValues.length - 1]);
      arrays.isConnectedValues.push(arrays.isConnectedValues[arrays.isConnectedValues.length - 1]);
    }

    // Return an array of data series formatted for uPlot.
    return [
      arrays.timestamps,
      arrays.temperatureCurrentValues,
      arrays.temperatureLowValues,
      arrays.temperatureHighValues,
      arrays.contactorStatusValues,
      arrays.overruleStatusValues,
      arrays.thresholdOnValues,
      arrays.thresholdOffValues,
      arrays.isConnectedValues
    ];
  }, [chartData]);
};

export default useFormattedData;
